
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Order } from '@/types';
import { formatCurrency } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface CashPaymentDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (order: Order, amount: number, change: number) => void;
}

const CashPaymentDialog: React.FC<CashPaymentDialogProps> = ({
  order,
  open,
  onOpenChange,
  onConfirm
}) => {
  const [receivedAmount, setReceivedAmount] = useState<string>('');
  const [change, setChange] = useState<number>(0);
  const isMobile = useIsMobile();
  
  // Reset values when dialog opens
  useEffect(() => {
    if (open) {
      setReceivedAmount(order.totalAmount.toString());
      setChange(0);
    }
  }, [open, order.totalAmount]);
  
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setReceivedAmount(value);
    
    // Calculate change
    const received = parseFloat(value) || 0;
    const changeAmount = received - order.totalAmount;
    setChange(changeAmount > 0 ? changeAmount : 0);
  };
  
  const handleConfirm = () => {
    const received = parseFloat(receivedAmount) || 0;
    if (received < order.totalAmount) {
      return; // Cannot confirm if received amount is less than total
    }
    
    // Store payment information directly in the order
    const updatedOrder = {
      ...order,
      paymentDetails: {
        method: 'cash',
        receivedAmount: received,
        change: change,
        date: new Date()
      },
      receivedAmount: received,
      changeAmount: change,
      status: 'paid' as const // Explicitly type as 'paid' to match Order type
    };
    
    onConfirm(updatedOrder, received, change);
    onOpenChange(false); // Make sure dialog closes after confirmation
  };
  
  // Create quick amount buttons with local currency values
  const suggestedAmounts = [
    Math.ceil(order.totalAmount / 10) * 10, // Round to next 10
    Math.ceil(order.totalAmount / 20) * 20, // Round to next 20
    Math.ceil(order.totalAmount / 50) * 50, // Round to next 50
    Math.ceil(order.totalAmount / 100) * 100 // Round to next 100
  ];
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "w-[90vw] max-w-[90vw] p-4" : ""}>
        <DialogHeader>
          <DialogTitle>Pagamento em Dinheiro</DialogTitle>
          <DialogDescription>
            Informe o valor recebido para calcular o troco
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="totalAmount">Valor Devido</Label>
            <div className="text-xl font-bold text-red-600">
              {formatCurrency(order.totalAmount)}
            </div>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="receivedAmount">Valor Recebido</Label>
            <Input 
              id="receivedAmount" 
              type="number" 
              step="0.01"
              min={order.totalAmount}
              value={receivedAmount}
              onChange={handleAmountChange}
              className="text-lg font-bold"
            />
          </div>
          
          <div className="flex flex-wrap gap-2">
            {suggestedAmounts.map(amount => (
              <Button 
                key={amount}
                variant="outline" 
                type="button" 
                onClick={() => {
                  setReceivedAmount(amount.toString());
                  setChange(amount - order.totalAmount);
                }}
              >
                R$ {amount.toFixed(2)}
              </Button>
            ))}
          </div>
          
          <div className="grid gap-2 pt-2 border-t">
            <Label htmlFor="change">Troco</Label>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(change)}
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={parseFloat(receivedAmount) < order.totalAmount}
          >
            Confirmar Pagamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CashPaymentDialog;
